import { useEffect, useState } from "react";
import { useSummarizationSocket } from "@/hooks/summarization_realtime/useSummarizationSocket";
import type { FileData } from "@/services/files";
import { CardContentTemplate } from "@/components/ui/CardTemplate";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { StatusFile } from "@/common/enums/enums";
import { toast } from "@/hooks/use-toast";
import { Alert } from "@/components/ui/alert";
import { CircleThinking } from "@/components/ui/CircleThinking";
import { AnimatedText } from "@/components/ui/AnimatedText";
import { t } from "i18next";
import { CardContentMainNote } from "@/pages/Visit/components/CardContentMainNote";

interface Props {
  file: FileData;
  refresh: () => void;
  isRegenerateEnabled: boolean;
}

export const CardContentTemplateGenerating = (
  props: Props
): React.ReactNode => {
  const [isThinking, setIsThinking] = useState<boolean>(true);
  const {
    result: resultSocket,
    isLoading: isLoadingSocket,
    error: errorSocket,
  } = useSummarizationSocket(props.file);

  const isFinished = resultSocket && !errorSocket && !isLoadingSocket;

  useEffect(() => {
    if (resultSocket) {
      setIsThinking(false);
    }
  }, [resultSocket]);

  useEffect(() => {
    if (resultSocket && !errorSocket && !isLoadingSocket) {
      toast({
        variant: "success",
        title: t(
          "visit.components.cardContentTemplateGenerating.noteGeneratedSuccessfully"
        ),
        duration: 1000,
      });
    }
  }, [isLoadingSocket, errorSocket, resultSocket]);

  if (
    props.file.status === StatusFile.GENERATING.toString() ||
    props.file.status === StatusFile.PROCESSING.toString()
  ) {
    return (
      <CardContentTemplate isOpen file={props.file}>
        {isThinking && !errorSocket && (
          <div className="flex gap-2 items-center">
            <CircleThinking />
            <AnimatedText duration={3} variant="blink">
              {`${t("visit.components.cardContentTemplateGenerating.processing")} ${props.file.template}: ${props.file.style} - ${props.file.format} - ${props.file.exam}`}
            </AnimatedText>
          </div>
        )}
        {errorSocket && <Alert variant="destructive">{errorSocket}</Alert>}
        <FileResultRendererMD result={resultSocket || ""} />
        {isFinished && (
          <AnimatedText className="mt-2" duration={2} variant="fade">
            {t(
              "visit.components.cardContentTemplateGenerating.textGeneratedSuccessfully"
            )}
          </AnimatedText>
        )}
      </CardContentTemplate>
    );
  }

  return (
    <CardContentMainNote
      file={props.file}
      isRegenerateEnabled={props.isRegenerateEnabled}
      refresh={props.refresh}
    />
  );
};
